import React, { useEffect, useMemo, useRef } from 'react';

import {
  getPendingNotifications,
  updateNotificationState,
} from '../../axios/Utils/notifications';
import { useAuth } from '../../context/AuthContext';
import { STORAGE_KEYS } from '../../hooks/Auth/useAuth';
import { useNotifications } from '../../hooks/Notifications/useNotifications';
import { useToast } from '../../hooks/UI/useToast';
import { useStorage } from '../../hooks/Utils/useStorage';
import {
  NotificationAck,
  NotificationRecord,
  NotificationsTransport,
} from '../../types/Notifications/types';
import {
  formatNotificationToastMessage,
  shouldDisplayNotificationToast,
} from '../../utils/notificationHelpers';
import { debugNotificationsLog } from '../../utils/notificationsProtocol';
import { createNotificationsTransport } from '../../utils/notificationsTransport';
import Toast from '../UI/Toast';

const showNotificationToast = (
  notification: NotificationRecord | undefined,
  showToast: ReturnType<typeof useToast>['showToast'],
): void => {
  if (!notification || !shouldDisplayNotificationToast(notification)) return;

  showToast({
    message: formatNotificationToastMessage(notification),
    color: 'success',
    duration: 4000,
  });
};

const logNotificationsSocketInfo = (message: string, payload?: unknown): void => {
  if (payload === undefined) {
    console.info(`[Notifications][socket.io] ${message}`);
    return;
  }

  console.info(`[Notifications][socket.io] ${message}`, payload);
};

const logNotificationsSocketWarning = (message: string, payload?: unknown): void => {
  if (payload === undefined) {
    console.warn(`[Notifications][socket.io] ${message}`);
    return;
  }

  console.warn(`[Notifications][socket.io] ${message}`, payload);
};

const NotificationsRealtimeHandler: React.FC = () => {
  const { loading, authStatus } = useAuth();
  const { getSecureValue } = useStorage();
  const { toastState, showToast, hideToast } = useToast();
  const {
    state,
    ingestRealtimeNotification,
    ingestSyncedNotifications,
    flushPendingAcks,
    setConnectionStatus,
    setRealtimeEnabled,
    setLastError,
  } = useNotifications();

  const transport = useMemo(() => createNotificationsTransport(), []);
  const transportRef = useRef<NotificationsTransport>(transport);
  const lastCursorRef = useRef<string | null>(state.lastCursor);

  useEffect(() => {
    lastCursorRef.current = state.lastCursor;
  }, [state.lastCursor]);

  useEffect(() => {
    if (loading || !authStatus || !state.pendingAcks.length) return undefined;

    const transportInstance = transportRef.current;
    const sendAck = async (ack: NotificationAck) => {
      if (state.connectionStatus === 'connected') {
        await transportInstance.sendAck(ack);
        return;
      }

      const success = await updateNotificationState(ack.id, ack.type);
      if (!success) {
        throw new Error(`No se pudo sincronizar el ACK ${ack.type} para ${ack.id}`);
      }
    };

    void flushPendingAcks(sendAck);
    return undefined;
  }, [
    authStatus,
    flushPendingAcks,
    loading,
    state.connectionStatus,
    state.pendingAcks.length,
  ]);

  useEffect(() => {
    if (loading) return undefined;

    const transportInstance = transportRef.current;
    let cancelled = false;
    let unsubscribers: Array<() => void> = [];

    const setupRealtime = async () => {
      if (!authStatus) {
        logNotificationsSocketInfo('Realtime deshabilitado porque no hay sesion activa');
        setRealtimeEnabled(false);
        setConnectionStatus('disconnected');
        setLastError(null);
        await transportInstance.disconnect();
        return;
      }

      const token = await getSecureValue(STORAGE_KEYS.token);

      if (!token) {
        logNotificationsSocketWarning(
          'No se encontro token de sesion para conectar realtime',
        );
        setRealtimeEnabled(false);
        setConnectionStatus('error');
        setLastError('No se encontro token de sesion para realtime');
        return;
      }

      logNotificationsSocketInfo('Preparando conexion realtime', {
        hasSessionToken: Boolean(token),
        lastCursor: lastCursorRef.current,
      });
      setRealtimeEnabled(true);
      setConnectionStatus('connecting');
      setLastError(null);

      const pendingNotifications = await getPendingNotifications();
      if (pendingNotifications.length) {
        logNotificationsSocketInfo(
          'Pendientes recuperadas por HTTP antes de conectar socket',
          {
            count: pendingNotifications.length,
          },
        );
        debugNotificationsLog('Pendientes recuperadas por HTTP', pendingNotifications);
        const newNotifications = ingestSyncedNotifications(
          pendingNotifications,
          pendingNotifications[0]?.id ? String(pendingNotifications[0].id) : null,
        );
        showNotificationToast(newNotifications[0], showToast);
      }

      unsubscribers = [
        transportInstance.on('connected', async () => {
          if (cancelled) return;
          logNotificationsSocketInfo('Conexion realtime establecida');
          setConnectionStatus('connected');
          setLastError(null);
          await transportInstance.sync(lastCursorRef.current);
        }),
        transportInstance.on('disconnected', (payload) => {
          if (cancelled) return;
          logNotificationsSocketWarning('Conexion realtime cerrada', payload);
          setConnectionStatus('disconnected');
          setLastError(payload?.reason ?? null);
          void (async () => {
            const pending = await getPendingNotifications();
            if (pending.length) {
              logNotificationsSocketInfo(
                'Pendientes recuperadas por HTTP tras desconexion',
                {
                  count: pending.length,
                },
              );
              const newNotifications = ingestSyncedNotifications(
                pending,
                pending[0]?.id ? String(pending[0].id) : null,
              );
              showNotificationToast(newNotifications[0], showToast);
            }
          })();
        }),
        transportInstance.on('error', (payload) => {
          if (cancelled) return;
          console.error('[Notifications][socket.io] Error de conexion realtime', payload);
          setConnectionStatus('error');
          setLastError(payload.message);
        }),
        transportInstance.on('notification', (payload) => {
          if (cancelled) return;
          logNotificationsSocketInfo('Evento notification:new recibido', payload);
          const notification = ingestRealtimeNotification(payload);
          showNotificationToast(notification, showToast);
        }),
        transportInstance.on('sync', (payload) => {
          if (cancelled) return;
          logNotificationsSocketInfo('Evento notifications:pending recibido', {
            count: payload.items.length,
            cursor: payload.cursor,
          });
          const newNotifications = ingestSyncedNotifications(
            payload.items,
            payload.cursor,
          );
          showNotificationToast(newNotifications[0], showToast);
        }),
      ];

      await transportInstance.connect({ token });
    };

    void setupRealtime();

    return () => {
      cancelled = true;
      unsubscribers.forEach((unsubscribe) => unsubscribe());
      void transportInstance.disconnect();
    };
  }, [
    authStatus,
    getSecureValue,
    ingestRealtimeNotification,
    ingestSyncedNotifications,
    loading,
    setConnectionStatus,
    setLastError,
    setRealtimeEnabled,
    showToast,
  ]);

  const handleToastDismiss = () => {
    hideToast();
  };

  return (
    <Toast
      isOpen={toastState.isOpen}
      message={toastState.message}
      onDidDismiss={handleToastDismiss}
      duration={toastState.duration}
      color={toastState.color}
    />
  );
};

export default NotificationsRealtimeHandler;
